import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Radio from '@material-ui/core/Radio';
import Button from 'components/common/Button';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import Form from 'components/form';
import Input from 'components/form/Input';
import {
  GET_ESSAY_TOPICS,
  EDIT_PROMPT,
  ADD_PROMPT,
  GET_ESSAY_PROMPTS,
} from 'graphql/essays';
import { TOPIC_TYPES } from 'constant/essay';
import { Query } from 'react-apollo';
import { useMutation } from '@apollo/react-hooks';
import Spin from 'components/common/Spin';
import sum from 'lodash/sum';
import compact from 'lodash/compact';
import useSnack from 'hooks/useSnack';

const { FormItem } = Form;
const useStyles = makeStyles((theme) => ({
  mainGrid: {
    'padding': theme.spacing(5),
    '& label': {
      fontWeight: 'bold',
    },
  },
  sideGrid: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(4),
    paddingTop: '30%',
    height: '100%',
  },
  heading: {
    marginBottom: theme.spacing(3),
  },
  infoIcon: {
    display: 'block',
    fontSize: 70,
    opacity: 0.7,
    marginBottom: theme.spacing(2),
  },
  closeIcon: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  paperWidthLg: {
    maxWidth: 1100,
  },
  filters: {
    '& > div': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
}));

const wordCountValidator = (rule, value, cb) => {
  if (value) {
    const count = compact(value.split(' ')).length;
    if (count > rule.limit) {
      cb(new Error(`Word limit: ${rule.limit}`));
    } else {
      cb();
    }
  } else {
    cb();
  }
};

const TopicStep = ({
  category,
  setStep,
  prompts,
  topics,
  setTopicId,
  topicId,
}) => {
  const classes = useStyles();
  const [filter, setFilter] = useState();

  return (
    <Box>
      <Box className={classes.filters} marginBottom={3}>
        <Chip
          clickable
          color="primary"
          label="All types"
          variant={!filter ? 'default' : 'outlined'}
          onClick={() => setFilter()}
        />
        {TOPIC_TYPES.map((i) => (
          <Chip
            key={i.value}
            clickable
            color={i.value === filter ? 'primary' : 'default'}
            variant={i.value === filter ? 'default' : 'outlined'}
            onClick={() => setFilter(i.value)}
            label={i.label}
          />
        ))}
      </Box>
      <TableContainer>
        <Table size={'small'}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Topic idea</TableCell>
              <TableCell align="right">Scores</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topics
              .filter((i) => (filter ? i.type === filter : true))
              .map((row) => {
                const isUsed = prompts.find((p) => p.topic_id === row.id);
                return (
                  <TableRow key={row.id}>
                    <TableCell padding="checkbox">
                      <Radio
                        size="small"
                        disabled={isUsed}
                        color={'primary'}
                        name="selected"
                        checked={isUsed || row.id === Number(topicId)}
                        onChange={(e) => setTopicId(row.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="textSecondary"
                      >
                        {row.idea}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="textSecondary">
                        {sum([row.impact, row.like, row.ease])} pt
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box textAlign="center" marginTop={3} marginBottom={2}>
        <Button
          onClick={() => setStep(2)}
          disabled={!topicId}
          variant="contained"
          color="primary"
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

const DetailsStep = ({
  onClose,
  form,
  student,
  category,
  topic,
  topicId,
  prompt,
}) => {
  const classes = useStyles();
  const snack = useSnack();
  const [addPrompt] = useMutation(ADD_PROMPT);
  const [updatePrompt] = useMutation(EDIT_PROMPT);
  const { getFieldDecorator, validateFields } = form;

  const handleSubmit = () => {
    return validateFields().then((values) => {
      if (prompt) {
        return updatePrompt({
          variables: {
            id: prompt.id,
            data: values,
          },
        }).then(
          () => {
            snack.success('Topic saved');
            onClose();
          },
          (e) => snack.error(e.message),
        );
      }
      return addPrompt({
        variables: {
          data: {
            ...values,
            user_id: student.id,
            category_id: category.id,
            topic_id: topicId,
          },
        },
        update: (proxy, mutationResult) => {
          const result = proxy.readQuery({
            query: GET_ESSAY_PROMPTS,
            variables: { userId: student.id },
          });
          if (result) {
            result.essay_prompts.push(mutationResult.data.row);
          }
          proxy.writeQuery({
            query: GET_ESSAY_PROMPTS,
            variables: { userId: student.id },
            data: {
              essay_prompts: result
                ? result.essay_prompts
                : [mutationResult.data.row],
            },
          });
        },
      }).then(
        () => {
          snack.success('Topic saved');
          onClose();
        },
        (e) => snack.error(e.message),
      );
    });
  };

  return (
    <Box>
      <Form>
        <FormItem>
          {getFieldDecorator('idea', {
            initialValue: prompt?.idea || topic?.idea,
            rules: [
              { required: true },
              { validator: wordCountValidator, limit: 15 },
            ],
          })(
            <Input
              multiline
              rows={2}
              label="Topic idea and overview (Limit: 15 words)"
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('situation', {
            initialValue: prompt?.situation,
            rules: [
              { required: true },
              { validator: wordCountValidator, limit: 15 },
            ],
          })(
            <Input
              multiline
              rows={2}
              label="Situation (Limit: 15 words)"
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('task', {
            initialValue: prompt?.task,
            rules: [
              { required: true },
              { validator: wordCountValidator, limit: 15 },
            ],
          })(<Input multiline rows={2} label="Task (Limit: 15 words)" />)}
        </FormItem>
        <FormItem>
          {getFieldDecorator('action', {
            initialValue: prompt?.action,
            rules: [
              { required: true },
              { validator: wordCountValidator, limit: 15 },
            ],
          })(<Input multiline rows={2} label="Action (Limit: 15 words)" />)}
        </FormItem>
        <FormItem>
          {getFieldDecorator('result', {
            initialValue: prompt?.result || topic?.outcome,
            rules: [
              { required: true },
              { validator: wordCountValidator, limit: 5 },
            ],
          })(
            <Input label="Result, achievement, outcome, (Limit: 5 words)" />,
          )}
        </FormItem>
      </Form>
      <Box textAlign="center" marginTop={3} marginBottom={2}>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

const DetailsStepForm = Form.create()(DetailsStep);

const AddPromptModal = ({ onClose, student, category, prompts, prompt }) => {
  const classes = useStyles();
  const [step, setStep] = useState(prompt ? 2 : 1);
  const [topicId, setTopicId] = useState();

  const title = step === 1 ? 'Select Topics' : 'Add Details';
  const description = step === 1 ? `Some students have a background, identity, interest, or talent that is so meaningful they believe their application would be incomplete without it. 
If this sounds like you, then please share your story.` : 'Elaborate on the story backwards using the STAR model:';

  return (
    <Dialog
      open
      disableBackdropClick
      fullWidth
      maxWidth={'sm'}
    >
      <DialogTitle disableTypography>
        <Box>
          <Typography gutterBottom variant="subtitle1">
            {title}
          </Typography>
          <Typography variant="caption">
            {description}
          </Typography>
          <IconButton
            className={classes.closeIcon}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Query query={GET_ESSAY_TOPICS} variables={{ userId: student.id }}>
          {({ data, loading }) => {
            if (loading) return <Spin spinning linear />;
            const { essay_topics } = data;

            if (step === 1)
              return (
                <TopicStep
                  category={category}
                  prompts={prompts}
                  onClose={onClose}
                  setStep={setStep}
                  topics={essay_topics}
                  topicId={topicId}
                  setTopicId={setTopicId}
                />
              );

            const topic = essay_topics.find((i) => i.id === topicId);

            return (
              <DetailsStepForm
                onClose={onClose}
                student={student}
                category={category}
                prompt={prompt}
                topicId={topicId}
                topic={topic}
              />
            );
          }}
        </Query>
      </DialogContent>
    </Dialog>
  );
};

export default AddPromptModal;
